import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { activeNavigation, LOGO } from "@/lib/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 shrink-0 transition-colors duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-soft" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="container-x flex items-center justify-between py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={LOGO} alt="Orrbbit" className="h-9 w-auto max-w-[180px] object-contain md:h-10" />
        </Link>

        <nav className="hidden items-center gap-4 xl:flex">
          {activeNavigation().map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `inline-flex min-h-[44px] items-center whitespace-nowrap text-[13px] font-medium transition-colors hover:text-teal ${
                  isActive ? "text-teal" : "text-bodygrey"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <Link
            to="/login"
            data-testid="nav-login-link"
            className="inline-flex min-h-[44px] items-center text-sm font-semibold text-navy transition-colors hover:text-teal"
          >
            Log In
          </Link>
          <Link
            to="/get-started"
            data-testid="nav-get-started-link"
            className="rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2"
          >
            Get Started
          </Link>
        </div>

        <button
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-navy xl:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="max-h-[calc(100dvh-72px)] overflow-y-auto border-t border-border bg-white xl:hidden"
          >
            <nav className="container-x flex flex-col py-3">
              {activeNavigation().map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-base font-medium ${
                      isActive ? "bg-lightbg text-teal" : "text-navy"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/login"
                data-testid="mobile-nav-login-link"
                className="mt-2 rounded-full border border-navy/15 px-4 py-3 text-center text-base font-semibold text-navy"
              >
                Log In
              </Link>
              <Link
                to="/get-started"
                data-testid="mobile-nav-get-started-link"
                className="mt-2 rounded-full bg-teal px-4 py-3 text-center text-base font-semibold text-white"
              >
                Get Started
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

